import { useEffect } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { getFirebaseMessaging } from '../firebase/firebaseConfig';
import { registerFcmToken } from '../services/fcmTokenService';

const VAPID_KEY = 'BOlUi62nMpSoj51vRCGVLV8qL13EO3K7jbuL-sigtshTqe9JciZdE4Y5HTSwzr48rbOMrCwO0FXjLVHSEzNZTLo';
const FCM_TOKEN_STORAGE_KEY = 'vidya-kasam:fcm-token';

// Feature toggle: Set to true to enable FCM push notifications, false to disable them entirely.
export const ENABLE_PUSH_NOTIFICATIONS = false;

/**
 * Gets the FCM token SILENTLY (no permission prompt).
 * Only returns a token if the user has already granted notification permission.
 * Safe to call before/during the login flow.
 * @returns {Promise<string|null>}
 */
export async function getFcmTokenSilently() {
  if (!ENABLE_PUSH_NOTIFICATIONS) {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (let registration of registrations) {
        if (registration.active?.scriptURL.includes('firebase-messaging-sw.js')) {
          await registration.unregister();
        }
      }
    }
    return null;
  }
  
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return null;
    }
    const messaging = await getFirebaseMessaging();
    if (!messaging) return null;

    let serviceWorkerRegistration = null;
    if ('serviceWorker' in navigator) {
      serviceWorkerRegistration = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js',
        { scope: '/' }
      );
    }

    return await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration });
  } catch {
    return null;
  }
}

/**
 * Hook that runs once the user is authenticated.
 * It requests notification permission (if not yet granted),
 * fetches the FCM token, sends it to the backend, and listens
 * for foreground messages.
 */
export function useFcmToken() {
  useEffect(() => {
    let unsubscribe = null;

    async function setupFcm() {
      if (!ENABLE_PUSH_NOTIFICATIONS) {
        // If disabled, actively unregister any existing service workers so background pushes stop
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (let registration of registrations) {
            if (registration.active?.scriptURL.includes('firebase-messaging-sw.js')) {
              await registration.unregister();
              console.info('[FCM] Unregistered existing service worker because push is disabled.');
            }
          }
        }
        return;
      }
      try {
        const messaging = await getFirebaseMessaging();
        if (!messaging) return;

        // Request permission (shows browser prompt if not yet decided)
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.info('[FCM] Notification permission denied.');
          return;
        }

        // Register service worker
        let serviceWorkerRegistration = null;
        if ('serviceWorker' in navigator) {
          serviceWorkerRegistration = await navigator.serviceWorker.register(
            '/firebase-messaging-sw.js',
            { scope: '/' }
          );
        }

        // Get FCM token
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration,
        });

        if (!token) {
          console.warn('[FCM] No registration token available.');
          return;
        }

        // Only send to backend if token changed
        const cachedToken = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
        if (token !== cachedToken) {
          await registerFcmToken(token);
          localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
          console.info('[FCM] Token registered with backend.');
        }

        // Foreground message → show a native browser notification
        unsubscribe = onMessage(messaging, (payload) => {
          const { title, body, icon } = payload.notification || {};
          if (Notification.permission === 'granted') {
            new Notification(title || 'Vidya Kasam', {
              body: body || '',
              icon: icon || '/logo192.png',
            });
          }
        });
      } catch (error) {
        console.error('[FCM] Setup failed:', error);
      }
    }

    setupFcm();
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, []);
}
